import {
  buildStripCrossesPanelDescriptor,
  buildStripHelpDescriptor,
} from "../messages/exportMessages.js";
import { prepareRawCuts } from "./cutSequence.js";

const TOLERANCE = 2;

export const STRIP_MODE_HELP = {
  whenToUse: {
    title: buildStripHelpDescriptor("whenToUse", "title"),
    body: buildStripHelpDescriptor("whenToUse", "body"),
    example: buildStripHelpDescriptor("whenToUse", "example"),
  },
  whenBlocked: {
    title: buildStripHelpDescriptor("whenBlocked", "title"),
    body: buildStripHelpDescriptor("whenBlocked", "body"),
    fallback: buildStripHelpDescriptor("whenBlocked", "fallback"),
  },
  noEligibleSheets: {
    title: buildStripHelpDescriptor("noEligibleSheets", "title"),
    body: buildStripHelpDescriptor("noEligibleSheets", "body"),
  },
};

function ripCrossesPanelInterior(cut, placement) {
  if (cut.axis === "horizontal") {
    if (cut.position <= placement.y + TOLERANCE
      || cut.position >= placement.y + placement.width - TOLERANCE) {
      return false;
    }

    return cut.start < placement.x + placement.length - TOLERANCE
      && cut.end > placement.x + TOLERANCE;
  }

  if (cut.position <= placement.x + TOLERANCE
    || cut.position >= placement.x + placement.length - TOLERANCE) {
    return false;
  }

  return cut.start < placement.y + placement.width - TOLERANCE
    && cut.end > placement.y + TOLERANCE;
}

/**
 * Check whether strip rips for this sheet would cut through unrelated panels.
 */
export function assessStripLayout(sheet, sourcePanel) {
  const placements = sheet.placements || [];

  if (!placements.length) {
    return {
      eligible: false,
      reason: "empty",
      summary: buildStripHelpDescriptor("emptySheet", "title"),
      detail: buildStripHelpDescriptor("emptySheet", "body"),
    };
  }

  const prepared = prepareRawCuts(sheet, sourcePanel, "strips", true);
  const crossingCut = prepared.rawCuts.find((cut) => (
    placements.some((placement) => ripCrossesPanelInterior(cut, placement))
  ));

  if (crossingCut) {
    const crossed = placements.find((placement) => ripCrossesPanelInterior(crossingCut, placement));
    const axisKey = crossingCut.axis === "horizontal" ? "horizontal" : "vertical";

    return {
      eligible: false,
      reason: "crosses-panel",
      summary: STRIP_MODE_HELP.whenBlocked.title,
      detail: buildStripCrossesPanelDescriptor(
        axisKey,
        crossingCut.position,
        crossed?.name || null,
      ),
      crossedPanelName: crossed?.name || null,
    };
  }

  return {
    eligible: true,
    reason: "ok",
    summary: STRIP_MODE_HELP.whenToUse.title,
    detail: STRIP_MODE_HELP.whenToUse.body,
  };
}

export function getStripLayoutWarning(sheet, sourcePanel) {
  const assessment = assessStripLayout(sheet, sourcePanel);
  if (assessment.eligible) return null;
  return assessment.detail || assessment.summary;
}

export function getStripEligibilityForLayout(layout, sourcePanel) {
  return (layout?.sheets || []).map((sheet) => ({
    sheet,
    sheetIndex: sheet.index,
    assessment: assessStripLayout(sheet, sourcePanel),
  }));
}

export function countStripEligibleSheets(layout, sourcePanel) {
  return getStripEligibilityForLayout(layout, sourcePanel)
    .filter((item) => item.assessment.eligible).length;
}

/**
 * Apply strip strategy only when the sheet layout supports it.
 */
export function resolveSheetStrategy(exportDraft, sheet, sourcePanel) {
  const requested = exportDraft.sheetStrategies?.[sheet.index] || "inherit";
  const requestedStrategy = requested === "inherit"
    ? exportDraft.globalStrategy
    : requested;

  if (requestedStrategy !== "strips") {
    return {
      strategy: requestedStrategy,
      requestedStrategy,
      stripBlocked: false,
      assessment: assessStripLayout(sheet, sourcePanel),
    };
  }

  const assessment = assessStripLayout(sheet, sourcePanel);
  if (assessment.eligible) {
    return {
      strategy: "strips",
      requestedStrategy,
      stripBlocked: false,
      assessment,
    };
  }

  return {
    strategy: "efficient_cutting",
    requestedStrategy,
    stripBlocked: true,
    assessment,
  };
}

export function isStripStrategyAllowed(exportDraft, sheet, sourcePanel) {
  if (exportDraft.sheetStrategies?.[sheet.index] === "strips") {
    return assessStripLayout(sheet, sourcePanel).eligible;
  }

  if (exportDraft.sheetStrategies?.[sheet.index]
    && exportDraft.sheetStrategies[sheet.index] !== "inherit") {
    return true;
  }

  if (exportDraft.globalStrategy !== "strips") {
    return true;
  }

  return assessStripLayout(sheet, sourcePanel).eligible;
}
